// fc-reveal.js — Framed Cars scroll-reveal motion system.
//
// A single global IntersectionObserver fades + rises any [data-fc-reveal] element into
// view once. A container marked [data-fc-reveal-group] is the trigger for its
// [data-fc-reveal] descendants, which cascade in with a 100ms stagger (design-system §5).
//
// Safety contract (matches the CSS in fc-tokens.liquid §8):
//   - Fail-safe: the hidden state is the JS-added `.fc-reveal--pending` class. If this
//     script never runs, nothing is hidden — content stays fully visible.
//   - Reduced-motion-safe: the hidden/animated CSS lives inside a
//     (prefers-reduced-motion: no-preference) query, and this script no-ops under reduce.
//   - No flicker: elements already in view on load are shown instantly (no entrance);
//     only content scrolled into view animates.

const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');
const STAGGER_STEP = 100; // ms between staggered group children
const ROOT_MARGIN = '0px 0px -10% 0px'; // fire when the trigger is ~10% into the viewport
const VIEW_RATIO = 0.9; // "already in view on load" threshold, kept in step with ROOT_MARGIN

let observer;

function startWillChange(el) {
  // Animating filter: blur() is expensive on large images (per-frame re-raster). Promote the
  // element to a GPU layer for the duration of the transition, then release it on transitionend
  // so we don't permanently hold a compositor layer (and its memory) on every blurred image.
  el.style.willChange = 'filter, transform';
  const done = (event) => {
    if (event.target !== el || event.propertyName !== 'filter') return;
    el.style.willChange = '';
    el.removeEventListener('transitionend', done);
  };
  el.addEventListener('transitionend', done);
}

function reveal(el, animating) {
  if (animating && el.classList.contains('fc-reveal--blur')) startWillChange(el);
  el.classList.remove('fc-reveal--pending');
  el.classList.add('fc-reveal--in');
}

function revealGroup(group, animating) {
  const items = [...group.querySelectorAll('[data-fc-reveal]')];
  items.forEach((item, i) => {
    item.style.setProperty('--fc-reveal-delay', `${i * STAGGER_STEP}ms`);
    reveal(item, animating);
  });
}

function hide(el) {
  el.classList.add('fc-reveal--pending');
}

function isGroup(el) {
  return el.hasAttribute('data-fc-reveal-group');
}

function isInView(el) {
  const rect = el.getBoundingClientRect();
  const vh = window.innerHeight || document.documentElement.clientHeight;
  return rect.top < vh * VIEW_RATIO && rect.bottom > 0;
}

function onIntersect(entries) {
  entries.forEach((entry) => {
    if (!entry.isIntersecting) return;
    observer.unobserve(entry.target);
    if (isGroup(entry.target)) revealGroup(entry.target, true);
    else reveal(entry.target, true);
  });
}

// Native Horizon product/resource grids get the fade-up reveal too. Rule 1 forbids editing
// Horizon core markup, so instead of hand-placing attributes we decorate the grid items from
// here at runtime: each card becomes a standalone [data-fc-reveal] that reveals as it scrolls in
// (so it works for any grid size, including infinite-scroll collections — late-added cards that
// miss decoration simply stay visible, which is the fail-safe default).
const NATIVE_GRID_ITEMS = '.product-grid__item, .resource-list__item';

function decorateNativeGrids(root) {
  root.querySelectorAll(NATIVE_GRID_ITEMS).forEach((el) => {
    if (!el.hasAttribute('data-fc-reveal')) el.setAttribute('data-fc-reveal', '');
  });
}

function collectTriggers(root) {
  // Groups stagger their descendants; standalone [data-fc-reveal] elements trigger themselves.
  const groups = [...root.querySelectorAll('[data-fc-reveal-group]')];
  const grouped = new Set();
  groups.forEach((g) => g.querySelectorAll('[data-fc-reveal]').forEach((c) => grouped.add(c)));
  const singles = [...root.querySelectorAll('[data-fc-reveal]')].filter((el) => !grouped.has(el));
  return [...groups, ...singles];
}

function init(root = document) {
  if (REDUCE.matches || !observer) return; // reduce-motion → leave content static + visible

  decorateNativeGrids(root);

  collectTriggers(root).forEach((trigger) => {
    if (isInView(trigger)) {
      // Visible on load → show instantly, no entrance, no flicker.
      if (isGroup(trigger)) revealGroup(trigger, false);
      else reveal(trigger, false);
      return;
    }
    // Below the fold → hide the affected element(s), then watch the trigger.
    if (isGroup(trigger)) trigger.querySelectorAll('[data-fc-reveal]').forEach(hide);
    else hide(trigger);
    observer.observe(trigger);
  });
}

if (!REDUCE.matches) {
  observer = new IntersectionObserver(onIntersect, { rootMargin: ROOT_MARGIN, threshold: 0 });
  init();
  // Theme editor: re-scan a section when it's added or re-rendered.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
}
